/**
 * Client writes allowed by firestore.rules on users/{uid} after server baseline:
 * - lastLogin on root (not in immutable deny list for self)
 * - firstName / lastName under users/{uid}/private/pii
 */
import { doc, writeBatch } from "firebase/firestore";
import { db } from "@/services/firebaseService";

export type MergeUserAllowedFieldsParams = {
  uid: string;
  firstName?: string;
  lastName?: string;
  lastLogin?: Date;
};

const isMeaningful = (value?: string): value is string =>
  typeof value === "string" && value.trim() !== "";

export async function mergeUserAllowedFieldsAfterSignup(params: MergeUserAllowedFieldsParams): Promise<void> {
  const { uid, firstName, lastName, lastLogin } = params;
  if (!uid) return;

  const batch = writeBatch(db);
  const userRef = doc(db, "users", uid);

  if (lastLogin !== undefined) {
    batch.set(userRef, { lastLogin }, { merge: true });
  }

  const piiData: Record<string, unknown> = {};
  // A blank incoming name means "the caller has nothing to say about this
  // field" — never "erase what is stored". These writes are merge writes, so
  // the previous `|| null` REPLACED a real surname with null on a path where
  // nothing reads back and nothing renders an error: /create-account's Google
  // button validates only the terms checkbox, so the typed surname is "" and a
  // one-word display name splits to "" as well. Omitting the key is what makes
  // the merge a no-op for that field. Whitespace counts as blank — writing
  // "   " over "Jones" is the same loss wearing a different value.
  if (isMeaningful(firstName)) {
    piiData.firstName = firstName;
  }
  if (isMeaningful(lastName)) {
    piiData.lastName = lastName;
  }
  if (Object.keys(piiData).length > 0) {
    const piiRef = doc(db, "users", uid, "private", "pii");
    batch.set(piiRef, piiData, { merge: true });
  }

  await batch.commit();
}
